import { useCallback, useState } from "react";

/**
 * Field limits and format checks for the admin forms.
 *
 * Lengths mirror the API's Zod schemas (`admin.dto.ts`, `domain-shared`), so a
 * form can say what is wrong before the round trip. Where the API accepts any
 * characters — names, phone, location — the patterns here are the panel's own
 * and deliberately stricter: they keep out the pasted junk the server would
 * happily store. The server stays the authority on everything else.
 *
 * Every checker takes the raw box value and returns null when it is fine, or a
 * short sentence to show under the field. They trim first, as the API does,
 * and treat an empty optional field as fine.
 */

export const LIMITS = {
  EMAIL_MAX: 255,
  /** The API allows 128; the panel keeps names short enough to read on one line. */
  NAME_MIN: 2,
  NAME_MAX: 64,
  PHONE_MIN: 4,
  PHONE_MAX: 24,
  LOCATION_MAX: 128,
  PASSWORD_MAX: 128,
  ROLE_KEY_MIN: 3,
  ROLE_KEY_MAX: 32,
  ROLE_NAME_MAX: 64,
  ROLE_DESCRIPTION_MAX: 500,
  SEARCH_MAX: 128,
  /** Same as the API: what one line of the in-game banner holds, and a few lines of message. */
  NOTIFICATION_TITLE_MAX: 40,
  NOTIFICATION_BODY_MAX: 300,
  /** Same as the API: a one-line headline, and a summary of a game change in the lobby card. */
  ANNOUNCEMENT_TITLE_MAX: 40,
  ANNOUNCEMENT_BODY_MAX: 1000,
} as const;

/** Something, an @, something, a dot, and a top-level part of two or more. */
const EMAIL_RE = /^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)*\.[^\s@.]{2,}$/;

/** Letters in any script, with spaces, apostrophes, dots and hyphens between. */
const NAME_RE = /^\p{L}[\p{L}\p{M} .'’-]*$/u;

/** An optional leading +, then digits with spaces, dashes or brackets. */
const PHONE_RE = /^\+?[\d\s()-]+$/;

/** Place names: letters, digits and the punctuation addresses use. */
const LOCATION_RE = /^[\p{L}\p{M}\d][\p{L}\p{M}\d\s.,'’()/-]*$/u;

/** Same rule the API applies in `validateRoleKey`. */
const ROLE_KEY_RE = /^[a-z][a-z0-9_]*$/;

/** Seeded roles. The API answers `400 INVALID_ROLE_KEY` for any of these. */
const RESERVED_ROLE_KEYS: ReadonlySet<string> = new Set([
  "super_admin",
  "game_designer",
  "live_ops",
  "support",
  "analyst",
]);

export function emailProblem(raw: string): string | null {
  const value = raw.trim();
  if (value === "") return "Enter an email address.";
  if (value.length > LIMITS.EMAIL_MAX) return `At most ${String(LIMITS.EMAIL_MAX)} characters.`;
  if (!EMAIL_RE.test(value)) return "Enter a valid email address, like name@company.com.";
  return null;
}

export function nameProblem(raw: string): string | null {
  const value = raw.trim();
  if (value === "") return "Enter a name.";
  if (value.length < LIMITS.NAME_MIN) return `At least ${String(LIMITS.NAME_MIN)} characters.`;
  if (value.length > LIMITS.NAME_MAX) return `At most ${String(LIMITS.NAME_MAX)} characters.`;
  if (!NAME_RE.test(value)) return "Use letters, spaces, apostrophes, dots and hyphens only.";
  // "Aaaaaaaaaa" passes the pattern and is never a real name.
  if (/(.)\1{3,}/u.test(value)) return "The same character repeats too many times.";
  return null;
}

/** Optional: empty is fine and means "clear it". */
export function phoneProblem(raw: string): string | null {
  const value = raw.trim();
  if (value === "") return null;
  if (value.length > LIMITS.PHONE_MAX) return `At most ${String(LIMITS.PHONE_MAX)} characters.`;
  if (!PHONE_RE.test(value)) return "Digits only, with an optional leading + and spaces or dashes.";
  const digits = value.replace(/\D/g, "").length;
  if (digits < 7 || digits > 15) return "A phone number has 7 to 15 digits.";
  return null;
}

/** Optional: empty is fine and means "clear it". */
export function locationProblem(raw: string): string | null {
  const value = raw.trim();
  if (value === "") return null;
  if (value.length > LIMITS.LOCATION_MAX) {
    return `At most ${String(LIMITS.LOCATION_MAX)} characters.`;
  }
  if (!LOCATION_RE.test(value)) return "Use letters, numbers, spaces and , . ' - ( ) / only.";
  return null;
}

export function roleKeyProblem(raw: string): string | null {
  const value = raw.trim();
  const min = String(LIMITS.ROLE_KEY_MIN);
  const max = String(LIMITS.ROLE_KEY_MAX);

  if (value === "") return "Enter a key for this role.";
  // Most specific first: "use lowercase" helps more than "invalid character"
  // when the only thing wrong is a capital letter.
  if (/\s/.test(value)) return "Spaces are not allowed. Use an underscore instead, e.g. qa_tester.";
  if (/[A-Z]/.test(value) && ROLE_KEY_RE.test(value.toLowerCase())) {
    return `Use lowercase letters only, e.g. ${value.toLowerCase()}.`;
  }
  if (!/^[a-z]/i.test(value)) return "The key must start with a letter (a–z).";
  if (!ROLE_KEY_RE.test(value.toLowerCase())) {
    return "Only lowercase letters (a–z), numbers (0–9) and underscores (_) are allowed.";
  }
  if (value.length < LIMITS.ROLE_KEY_MIN) return `The key must be at least ${min} characters long.`;
  if (value.length > LIMITS.ROLE_KEY_MAX) {
    return `The key can be at most ${max} characters long (it is ${String(value.length)}).`;
  }
  if (RESERVED_ROLE_KEYS.has(value)) {
    return `"${value}" is used by a built-in role. Choose a different key.`;
  }
  return null;
}

export function roleNameProblem(raw: string): string | null {
  const value = raw.trim();
  if (value === "") return "Enter a display name.";
  if (value.length > LIMITS.ROLE_NAME_MAX)
    return `At most ${String(LIMITS.ROLE_NAME_MAX)} characters.`;
  return null;
}

export function roleDescriptionProblem(raw: string): string | null {
  if (raw.trim().length > LIMITS.ROLE_DESCRIPTION_MAX) {
    return `At most ${String(LIMITS.ROLE_DESCRIPTION_MAX)} characters.`;
  }
  return null;
}

/**
 * When to show a field's problem.
 *
 * After the person leaves the box, not while they are typing — "enter a valid
 * email" under the first letter of one is noise. A field that opened with a
 * value is shown straight away: that value is saved data, and a save button
 * that is disabled for no stated reason is worse than an early red line.
 */
export function useTouched<K extends string>(prefilled: Partial<Record<K, string | null>> = {}) {
  const [touched, setTouched] = useState<ReadonlySet<K>>(
    () =>
      new Set(
        (Object.keys(prefilled) as K[]).filter((key) => (prefilled[key] ?? "").trim() !== ""),
      ),
  );

  const blur = useCallback(
    (key: K) => () =>
      setTouched((current) => (current.has(key) ? current : new Set(current).add(key))),
    [],
  );

  const show = (key: K, problem: string | null): string | undefined =>
    touched.has(key) ? (problem ?? undefined) : undefined;

  const reset = useCallback((keys: readonly K[] = []) => setTouched(new Set(keys)), []);

  return { blur, show, reset };
}
